/**
 * @fileoverview Display ordering for "amount owed" estimate cards.
 * @module components/jobs/phases/gather/summarization/shared-panels/owedEstimateOrder
 *
 * Purpose:
 * - Both the Creditors (unsecured) and Security Interests (secured creditors)
 *   panels show competing views of what a creditor is owed as a list of estimate
 *   cards. Practitioners trust a figure that comes from the creditor themselves —
 *   a Proof of Debt (or an emailed claim) and its adjudicated amount — over one
 *   read out of the company's own books and records. This orders the cards so
 *   the creditor-sourced figures sit at the top, above the company-records
 *   figure.
 *
 * Why this is separate from the backend:
 * - This is presentational only. Which estimate becomes the endorsed "amount
 *   owed" of record is resolved separately on the backend (`deriveOwed` /
 *   `CREDITOR_SOURCE_RANK`) and is deliberately conservative — a creditor's
 *   unverified claim must not silently inflate the ledger. Ordering the cards
 *   for the eye does not change which figure the job runs on.
 *
 * Used by:
 * - generalLiabilities/OwedEstimatesSection.tsx (unsecured creditors)
 * - securityInterests/OwedEstimatesSection.tsx (secured creditors)
 */

/**
 * How prominently each provenance is shown — higher renders first (top).
 * Creditor-sourced figures (a Proof of Debt and its adjudicated amount) outrank
 * the company's books and records, with the practitioner's own estimate between.
 */
export const OWED_SOURCE_DISPLAY_RANK: Record<string, number> = {
  pod_admitted: 4,
  proof_of_debt: 3,
  manual: 2,
  document: 1,
};

function displayRank(source: string): number {
  return OWED_SOURCE_DISPLAY_RANK[source] ?? 0;
}

/** The subset of an owed estimate this comparator needs to order the list. */
export interface OwedEstimateForDisplay {
  source: string;
  asAtDate?: string;
  recordedAt: number;
}

/**
 * Sort comparator for owed-estimate cards: creditor-sourced figures first, then
 * the most recent as-at date, then the most recently recorded. Same-source
 * cards keep a sensible, stable-feeling order.
 *
 * @example
 * const ordered = [...cards].sort(compareOwedEstimatesForDisplay);
 */
export function compareOwedEstimatesForDisplay(
  a: OwedEstimateForDisplay,
  b: OwedEstimateForDisplay,
): number {
  const byRank = displayRank(b.source) - displayRank(a.source);
  if (byRank !== 0) return byRank;
  const dateA = a.asAtDate ?? "";
  const dateB = b.asAtDate ?? "";
  if (dateA !== dateB) return dateB.localeCompare(dateA);
  return b.recordedAt - a.recordedAt;
}
